const WHITE = "#ffffff"
const WHITE_200 = "hsl(192 38% 97%)"
const YELLOW_200 = "hsl(60 80% 70%)"
const YELLOW_400 = "hsl(55 87% 50%)"
const GREEN_200 = "hsl(110 80% 80%)"
const BLACK = "#000"
const GRAY_080 = "hsl(200 10% 99%)"
const GRAY_100 = "hsl(200 10% 97%)"
const GRAY_150 = "hsl(200 10% 95%)"
const GRAY_200 = "hsl(200 10% 94%)"
const GRAY_250 = "hsl(200 10% 90%)"
const GRAY_300 = "hsl(200 10% 85%)"
const GRAY_400 = "hsl(200 10% 60%)"
const GRAY_600 = "hsl(200 10% 40%)"
const GRAY_900 = "hsl(200 10% 10%)"
const COLOR_YELLOW = "hsl(55deg 100% 60%)"
const COLOR_GREEN = "hsl(130deg 100% 80%)"
const COLOR_CYAN = "#009FE3"
const COLOR_NEON_CYAN = "rgba(128, 255, 251, 1)"
const COLOR_PURPLE = "#554897"
const COLOR_BLACK = "#000000"

const theme = {
  breakpoints: ["40em", "52em", "64em"],

  space: [0, 4, 8, 12, 16, 20, 24, 32, 64, 128, 256, 512],

  fonts: {
    body: '"IBM Plex Sans", sans-serif',
    heading: '"IBM Plex Sans", sans-serif',
    monospace: '"IBM Plex Mono", monospace',
  },

  // 1.250 Major Third
  fontSizes: [8.19, 10.24, 12.8, 16, 20, 25, 31.25, 39.06, 48.83],

  fontWeights: {
    regular: 400,
    medium: 500,
    semiBold: 600,
  },

  lineHeights: {
    solid: 1,
    body: 1.65,
    heading: 1.15,
  },

  letterSpacings: {
    body: "normal",
  },

  colors: {
    background: WHITE,
    backgroundMuted: GRAY_300,
    backgroundNavbar: GRAY_300,
    backgroundSecondary: "#0F2938",
    border: GRAY_200,
    gray080: GRAY_080,
    gray100: GRAY_100,
    gray150: GRAY_150,
    gray200: GRAY_200,
    gray300: GRAY_300,
    grayDark: "hsl(0 0% 46% / 1)",
    highlight: COLOR_NEON_CYAN,
    muted: GRAY_150,
    outline: COLOR_CYAN,
    primary: COLOR_PURPLE,
    primaryAccent: "#DFDAB2",
    row: GRAY_080,
    rowHover: GRAY_100,
    secondary: COLOR_CYAN,
    success: COLOR_GREEN,
    table: GRAY_250,
    tertiary: COLOR_YELLOW,
    tertiaryAccent: "#DCE0E7",
    text: COLOR_BLACK,
    textDeactive: GRAY_300,
    textInverse: WHITE,
    textMuted: GRAY_600,
    textPlaceholder: GRAY_400,
    warning: COLOR_YELLOW,
    white: WHITE,
  },

  text: {
    default: {
      color: "text",
      fontFamily: "body",
      fontWeight: "regular",
      lineHeight: "body",
    },

    body: {
      fontSize: 3,
      lineHeight: "body",
    },

    small: {
      fontSize: 2,
      lineHeight: "body",
    },

    micro: {
      fontSize: 1,
      lineHeight: "body",
    },

    h1: {
      fontSize: 8,
      letterSpacings: "-0.1em",
    },

    h2: {
      fontSize: 7,
    },

    h3: {
      fontSize: 6,
    },

    h4: {
      fontSize: 5,
    },

    h5: {
      fontSize: 4,
    },

    h6: {
      fontSize: 3,
    },
  },

  shadows: {
    medium: `0 2px 4px ${GRAY_150}`,
    large: `0 1px 2px rgba(60,64,67, 0.3), 0 1px 3px 1px rgba(60,64,67, 0.15)`,
    navbar: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    outline: `inset 0 0 0 2px ${COLOR_CYAN}`,
  },

  borders: {
    outline: theme => `1px solid ${theme.color.border}`,
  },

  badges: {
    default: {
      fontSize: 1,
      fontWeight: "regular",
      lineHeight: "solid",
      p: 2,
      py: 1,
      borderRadius: "full",
    },
    success: {
      variant: "badges.default",
      color: "text",
      bg: "success",
    },
    warning: {
      variant: "badges.default",
      color: "text",
      bg: "warning",
    },
    muted: {
      variant: "badges.default",
      color: "text",
      bg: "backgroundMuted",
    },
  },

  radii: {
    small: 3,
    medium: 6,
    large: 9,
    full: 9999,
  },

  styles: {
    root: {
      fontFamily: "body",
      fontWeight: "regular",
    },
  },

  code: {
    fontFamily: "monospace",
    fontSize: "inherit",
  },

  table: {
    border: `1px solid`,
    borderColor: "table",
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    borderRadius: "large",
    lineHeight: "heading",
    bg: "white",

    "tr:nth-of-type(even)": {
      boxShadow: theme =>
        `inset 0 1px 0 0 ${theme.colors.table}, inset 0 -1px 0 0 ${theme.colors.table}`,
    },

    "&:hover tr:nth-of-type(even)": {
      bg: "transparent",
    },

    "&:not(:hover) tr:nth-of-type(even)": {
      bg: "row",
    },

    "tr:hover, tr:nth-of-type(even):hover": {
      bg: "rowHover",
    },

    tr: {
      border: `1px solid`,
      borderColor: "transparent",
    },

    thead: {
      tr: {
        th: {
          py: 4,
        },
      },
    },

    td: {
      py: 3,
      px: 3,
      pb: 4,
    },

    th: {
      px: 3,
      fontSize: 2,
      fontWeight: "medium",
      color: "textDeactive",
    },
  },

  mark: {
    bg: "red",
  },

  icon: {
    primary: {
      color: "text",
      bg: "transparent",
      border: theme => `1px solid ${theme.colors.table}`,
      "&:hover": {
        bg: "white",
      },
      "&:active": {
        bg: "gray150",
      },
    },
  },

  forms: {
    input: {
      fontFamily: "body",
      lineHeight: "42px",
      height: 42,
      borderRadius: "medium",
      borderWidth: 2,
      fontSize: 3,
      pl: [3],
      borderColor: "grayDark",
      "::placeholder": {
        color: "textPlaceholder",
      },
      ":focus": {
        color: "text",
        borderColor: "secondary",
        bg: "background",
      },
    },
  },

  layout: {
    container: {
      p: 9,
    },
  },

  tooltip: {
    default: {
      backgroundColor: "backgroundSecondary",
      borderRadius: "medium",
      boxShadow: "tooltip",
      color: "textInverse",
      maxWidth: 16,
      p: 3,
      variant: "text.small",
      arrow: {
        color: "backgroundSecondary",
      },
    },
  },

  images: {
    avatar: {
      width: 32,
      height: 32,
      borderRadius: "full",
      flexShrink: 0,
    },
  },
}

export default theme
